import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generate a public share URL from a shareId
 */
export function getPublicShareUrl(shareId: string): string {
  // Always use the dedicated public shares domain
  return `https://share.grupokingdom.com/shares/${shareId}`
}

/**
 * Generate a relative public share URL from a shareId
 */
export function getRelativeShareUrl(shareId: string): string {
  return `/shares/${shareId}`
}

/**
 * Format a number as currency
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

/**
 * Format a date string or Date object
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateObj)
}

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

/**
 * Calculate relative luminance of a color
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const val = c / 255
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Calculate contrast ratio between two colors
 */
function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1)
  const rgb2 = hexToRgb(color2)

  if (!rgb1 || !rgb2) return 0

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b)
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b)

  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)

  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Generate accessible text and border colors for a background color
 */
export function getAccessibleColors(backgroundColor: string): {
  text: string
  border: string
} {
  const rgb = hexToRgb(backgroundColor)
  if (!rgb) {
    return { text: '#000000', border: '#e5e5e5' }
  }

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b)

  // If the background is light, use dark text
  // If the background is dark, use light text
  const text = luminance > 0.5 ? '#000000' : '#ffffff'
  const border = luminance > 0.5 ? '#e5e5e5' : '#404040'

  return { text, border }
}

/**
 * Check WCAG 2.1 compliance for color contrast
 */
export function checkWCAGCompliance(
  foreground: string,
  background: string
): {
  ratio: number
  passesAA: boolean
  passesAAA: boolean
  passesCustom: boolean
} {
  const ratio = getContrastRatio(foreground, background)

  return {
    ratio,
    passesAA: ratio >= 4.5, // WCAG AA standard for normal text
    passesAAA: ratio >= 7, // WCAG AAA standard for normal text
    passesCustom: ratio >= 3, // Custom threshold (minimum for large text)
  }
}
